
import React from 'react';
import { motion } from 'framer-motion';
import { MenuItem, Language } from '../types';
import { StarIcon } from './icons';
import { useSettings } from '../src/contexts/SettingsContext';
import { DEFAULT_MENU_ITEM_IMAGE } from '../src/constants';
import { t } from '../translations';

interface FeaturedDishesProps {
  items: MenuItem[];
  language: Language;
  onSelect: (item: MenuItem) => void;
}

const FeaturedDishes: React.FC<FeaturedDishesProps> = ({ items, language, onSelect }) => {
  const { settings } = useSettings();
  
  if (items.length === 0) return null;

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4 px-1">
        <h2 className="text-xl font-black font-serif text-[var(--text-primary)] tracking-tight">
          {t('menuHeader.recommended', language)}
        </h2>
        <div className="h-[2px] flex-1 mx-4 bg-gradient-to-r from-[var(--accent-color)] to-transparent opacity-20"></div>
      </div>
      
      <div className="flex overflow-x-auto no-scrollbar -mx-5 px-5 space-x-4 pb-4">
        {items.map((item) => (
          <motion.div
            key={item.id}
            whileTap={{ scale: 0.98 }}
            onClick={() => onSelect(item)}
            className="flex-shrink-0 w-[280px] bg-[var(--bg-card)] rounded-3xl overflow-hidden shadow-[var(--shadow-soft)] border border-[var(--glass-border)] group"
          >
            <div className="relative h-40 overflow-hidden">
              <img
                src={item.image || DEFAULT_MENU_ITEM_IMAGE}
                alt={item.names?.[language] || item.names?.ru}
                className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110"
                loading="lazy"
                referrerPolicy="no-referrer"
                style={{ background: 'linear-gradient(135deg, var(--bg-tertiary) 0%, var(--bg-secondary) 100%)' }}
                onLoad={(e) => { (e.target as HTMLImageElement).style.background = 'none'; }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
              <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-md text-[var(--accent-color)] text-[10px] font-black px-2 py-1 rounded-full flex items-center gap-1 shadow-lg">
                <StarIcon className="w-3 h-3 fill-current" />
                <span>{t('menuHeader.featured', language)}</span>
              </div>
              <div className="absolute bottom-3 left-4 right-4">
                <p className="text-white font-black text-lg leading-tight drop-shadow-md">
                  {item.names?.[language] || item.names?.ru}
                </p>
              </div>
            </div>
            <div className="p-4 flex items-center justify-between">
              <div className="flex flex-col">
                <span className="text-[10px] text-[var(--text-secondary)] uppercase font-bold tracking-widest opacity-60">
                  {item.category}
                </span>
                <span className="text-lg font-black text-[var(--accent-color)]">
                  {item.price} {settings.currency}
                </span>
              </div>
              <button className="w-10 h-10 bg-[var(--accent-color)] text-white rounded-2xl flex items-center justify-center shadow-lg active:scale-90 transition-all">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor" className="w-5 h-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                </svg>
              </button>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default FeaturedDishes;
